import { CommonModule } from '@angular/common';
import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	computed,
	Inject,
	OnDestroy,
	OnInit
} from '@angular/core';
import { TranslocoModule } from '@jsverse/transloco';
import { Subject, takeUntil } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';

import {
	DEVICE_DISCOVERY_CLAIMING_SERVICE,
	DeviceClaimingDiscoveryApiService
} from '@shure/cloud/device-management/devices/data-access';
import { DeviceDetection } from '@shure/cloud/shared/models/devices';
import { OrganizationsStoreService } from '@shure/cloud/shared/services/organizations-store-service';
import { SwitchOrganizationService } from '@shure/cloud/shared/switch-organization/feature-switch-organization';
import { PipesModule } from '@shure/cloud/shared/ui/pipes';
import {
	PrismButtonComponent,
	PrismIconComponent,
	PrismLabelValueComponent,
	PrismProgressIndicatorComponent
} from '@shure/prism-angular-components';
import { PLACEHOLDER_VALUE } from '@shure/shared/models';

import { SelectedLocalNetworkDeviceIdMap } from '../../../../models/step2-discover-devices.models';
import {
	AddDevicesDialogService,
	DeviceClaimMethods,
	MacSerialNumberDevice
} from '../../../../services/add-devices-dialog.service';
import { ClaimingFlowService } from '../../../../services/claiming-flow.service';

import { LocalNetworkTableComponent } from './local-network-table/local-network-table.component';
import { MacSerialTableComponent } from './mac-serial-table/mac-serial-table.component';
import { QrCodeScanStepComponent } from './qr-code-scan-step/qr-code-scan-step.component';
import { QrCodeClaimStep, Step2DiscoverDevicesConfig } from './step2-discover-devices.config';

@Component({
	selector: 'sh-scan-local-network',
	imports: [
		CommonModule,
		LocalNetworkTableComponent,
		MacSerialTableComponent,
		TranslocoModule,
		PipesModule,
		PrismProgressIndicatorComponent,
		QrCodeScanStepComponent,
		PrismButtonComponent,
		PrismLabelValueComponent,
		PrismIconComponent
	],
	templateUrl: './step2-discover-devices.component.html',
	styleUrls: ['./step2-discover-devices.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class Step2DiscoverDevicesComponent implements OnInit, OnDestroy {
	public destroy$: Subject<boolean> = new Subject<boolean>();

	public discoveredDevices$ = this.deviceClaimingDiscoveryService.unclaimedDevices$.asObservable();
	public discoveredDevices: DeviceDetection[] = [];
	public loadingData = false;
	public numberSelected = 0;
	public selectedDeviceIds: string[] = [];
	public claimingMethod: DeviceClaimMethods | undefined;
	public readonly qrCodeClaimStepEnum = QrCodeClaimStep;

	public readonly organizationName = computed(() => {
		const orgName =
			this.organizationsStore.accountInformation()?.organizationDetails?.organizationName ?? PLACEHOLDER_VALUE;
		return orgName;
	});

	// This component gets instantiated at the moment the user navigates to the second step.
	// If the user leaves this step, the assumption is this component will get destroyed
	constructor(
		@Inject(DEVICE_DISCOVERY_CLAIMING_SERVICE)
		protected readonly deviceClaimingDiscoveryService: DeviceClaimingDiscoveryApiService,
		protected readonly addDevicesDialogService: AddDevicesDialogService,
		private readonly claimingFlowService: ClaimingFlowService,
		private readonly changeDetectorRef: ChangeDetectorRef,
		public readonly stepConfig: Step2DiscoverDevicesConfig,
		private readonly organizationsStore: OrganizationsStoreService,
		private readonly switchOrganizationService: SwitchOrganizationService
	) {}

	public onChangeOrganizationClick(): void {
		if (this.stepConfig.isClaimByLinkSelection()) {
			const candidateDevices = this.stepConfig.extractedDevices().map((entry) => ({
				mac: entry.device.macAddress,
				serial: entry.device.serialNumber
			}));
			this.claimingFlowService.setDevices(candidateDevices);
			this.claimingFlowService.setGoToClaimLinkSelectionStepFlag();
		}

		this.switchOrganizationService.openChangeOrgDialog();
	}

	public ngOnInit(): void {
		this.addDevicesDialogService.deviceDiscoveryMethod$.pipe(takeUntil(this.destroy$)).subscribe((claimMethod) => {
			this.claimingMethod = claimMethod;
			switch (claimMethod) {
				case 'ClaimByTokenOnLocalNetwork':
					this.initForLocalClaiming();
					break;
				case 'ClaimByMacSerialCsv':
				case 'ClaimByMacSerialSingle':
					this.initForMacSerialClaiming();
					break;
				case 'ClaimByQrCode':
					this.initForQrCodeClaiming();
					break;
			}
		});
	}

	public initForLocalClaiming(): void {
		this.deviceClaimingDiscoveryService.newScan();
	}

	public initForMacSerialClaiming(): void {
		// for CSV files with many entires, it can take a few seconds for the table to get updated
		// Set a boolean flag to indicate when loading has started and finished.
		this.loadingData = true;
		setTimeout(() => {
			this.stepConfig.extractedDevices.set(
				this.addDevicesDialogService.macSerialNumbers.map((macSn) => ({
					device: {
						id: uuidv4(),
						...macSn
					}
				}))
			);

			this.changeDetectorRef.markForCheck();
			this.loadingData = false;
		});
	}

	public initForQrCodeClaiming(): void {
		this.stepConfig.activeQrCodeClaimStep.set(QrCodeClaimStep.Scan);
	}

	public ngOnDestroy(): void {
		this.deviceClaimingDiscoveryService.destroy();
		this.destroy$.next(true);
		this.destroy$.complete();
	}

	public onSelectionChange(selection: SelectedLocalNetworkDeviceIdMap): void {
		this.selectedDeviceIds = [];
		for (let i = 0; i < Object.keys(selection.devices).length; i++) {
			this.selectedDeviceIds.push(Object.keys(selection.devices)[i]);
		}

		this.stepConfig.selectionCount.set(this.selectedDeviceIds.length);
		this.doSelectedDevicesNotifications();
	}

	public doSelectedDevicesNotifications(): void {
		if (this.claimingMethod === 'ClaimByTokenOnLocalNetwork') {
			this.discoveredDevices$.pipe(takeUntil(this.destroy$)).subscribe((devices) => {
				const discoveredDevices = devices.filter((row) => this.selectedDeviceIds.includes(row.device.id));
				this.addDevicesDialogService.setDetectedClaimableDevices(discoveredDevices);
			});
		} else {
			this.addDevicesDialogService.setMacSerialNumbersToClaim(
				<MacSerialNumberDevice[]>(
					this.stepConfig.extractedDevices().filter((row) => this.selectedDeviceIds.includes(row.device.id))
				)
			);
		}
	}
}
